/**
 * PoseVisualization.js
 * 3D visualization module for rendering human pose data from exercise tracking.
 * Provides an interactive Three.js based rendering of body landmarks and their connections.
 * Written by Punit Vats
 */

import * as THREE from 'three';

class PoseVisualizer {
  constructor(canvas) {
    this.canvas = canvas;
    
    // Set canvas to fill container
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    
    // Scene setup with light background
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xffffff);
    
    // Camera with fixed position
    this.camera = new THREE.PerspectiveCamera(45, canvas.width / canvas.height, 0.1, 1000);
    this.camera.position.set(1.5, 1.5, 3.0); // Position camera off-center
    this.camera.lookAt(0, 1, 0);
    
    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      alpha: true
    });
    this.renderer.setSize(canvas.width, canvas.height);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    
    // Add ambient light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
    this.scene.add(ambientLight);
    
    // Add directional light for better shadows
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(1, 2, 1);
    this.scene.add(directionalLight);
    
    // Add a second directional light from another angle
    const secondaryLight = new THREE.DirectionalLight(0xffffff, 0.5);
    secondaryLight.position.set(-1, 1, -1);
    this.scene.add(secondaryLight);
    
    // Create 3D environment with walls and floor
    this.createEnvironment();
    
    // Storage for visualization elements
    this.joints = [];
    this.bones = [];
    this.labels = [];
    this.bodyMeshes = [];
    this.pointMeshes = {}; // Store meshes by landmark index
    
    // Connections for a simplified human figure
    this.connections = [
      // Torso
      [11, 12], // shoulders
      [12, 24], // right shoulder to right hip
      [11, 23], // left shoulder to left hip
      [23, 24], // hips
      
      // Arms
      [11, 13], // left shoulder to left elbow
      [13, 15], // left elbow to left wrist
      [12, 14], // right shoulder to right elbow
      [14, 16], // right elbow to right wrist
      
      // Legs
      [23, 25], // left hip to left knee
      [25, 27], // left knee to left ankle
      [24, 26], // right hip to right knee
      [26, 28], // right knee to right ankle
    ];
    
    // Body part names for labels
    this.bodyPartNames = {
      0: 'Nose',
      11: 'L.Shoulder',
      12: 'R.Shoulder',
      13: 'L.Elbow',
      14: 'R.Elbow',
      15: 'L.Wrist',
      16: 'R.Wrist',
      23: 'L.Hip',
      24: 'R.Hip',
      25: 'L.Knee',
      26: 'R.Knee',
      27: 'L.Ankle',
      28: 'R.Ankle'
    };
    
    // Add zoom parameters
    this.zoomLevel = 1.0;
    this.minZoom = 0.5;
    this.maxZoom = 4.0;
    this.zoomSpeed = 0.1;
    
    // Add mouse wheel listener for zoom
    this.canvas.addEventListener('wheel', this.handleZoom.bind(this));
    
    // Start animation loop
    this.animate();
    
    // Handle window resize
    window.addEventListener('resize', () => this.onWindowResize());
    this.onWindowResize();
    
    // Add zoom controls
    this.setupZoomControls();
  }
  
  /**
   * createEnvironment - Sets up the 3D scene environment
   * Creates floor grid and reference walls for better spatial context
   */
  createEnvironment() {
    const size = 1.5;
    
    // Create a grid helper for the floor
    const gridHelper = new THREE.GridHelper(size * 2, 10, 0xaaaaaa, 0xdddddd);
    gridHelper.position.y = 0;
    this.scene.add(gridHelper);
    
    // Create walls with grid lines
    this.createWalls(size);
  }
  
  /**
   * createWalls - Generates semi-transparent walls for the environment
   * Creates visual reference planes with grid lines
   */
  createWalls(size) {
    // Create semi-transparent walls
    const wallMaterial = new THREE.MeshBasicMaterial({
      color: 0xf5f5f5,
      transparent: true,
      opacity: 0.3,
      side: THREE.DoubleSide
    });
    
    // Back wall (Z-axis)
    const backWall = new THREE.Mesh(
      new THREE.PlaneGeometry(size * 2, size * 2),
      wallMaterial
    );
    backWall.position.z = -size;
    backWall.position.y = size;
    this.scene.add(backWall);
    
    // Left wall (X-axis)
    const leftWall = new THREE.Mesh(
      new THREE.PlaneGeometry(size * 2, size * 2),
      wallMaterial
    );
    leftWall.position.x = -size;
    leftWall.position.y = size;
    leftWall.rotation.y = Math.PI / 2;
    this.scene.add(leftWall);
    
    // Add grid lines to walls
    this.addGridToWall(backWall, size);
    this.addGridToWall(leftWall, size);
  }
  
  /**
   * addGridToWall - Adds grid lines to a wall plane
   * Creates reference lines for better spatial perception
   */
  addGridToWall(wall, size) {
    const divisions = 10;
    const step = (size * 2) / divisions;
    const halfSize = size;
    
    // Create grid lines material
    const lineMaterial = new THREE.LineBasicMaterial({ 
      color: 0xaaaaaa,
      transparent: true,
      opacity: 0.5
    });
    
    // Horizontal lines
    for (let i = 0; i <= divisions; i++) {
      const y = -halfSize + i * step;
      const points = [
        new THREE.Vector3(-halfSize, y, 0),
        new THREE.Vector3(halfSize, y, 0)
      ];
      
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, lineMaterial);
      wall.add(line);
    }
    
    // Vertical lines
    for (let i = 0; i <= divisions; i++) {
      const x = -halfSize + i * step;
      const points = [
        new THREE.Vector3(x, -halfSize, 0),
        new THREE.Vector3(x, halfSize, 0)
      ];
      
      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const line = new THREE.Line(geometry, lineMaterial);
      wall.add(line);
    }
  }
  
  /**
   * setupZoomControls - Sets up interactive zoom controls
   * Adds keyboard and mouse wheel event listeners for zooming
   */
  setupZoomControls() {
    // Store bound keyboard handler for cleanup
    this.handleKeyDownBound = (event) => {
      if (event.key === '+' || event.key === '=') {
        this.zoomIn();
      } else if (event.key === '-' || event.key === '_') {
        this.zoomOut();
      }
    };
    
    window.addEventListener('keydown', this.handleKeyDownBound);
    
    // NOTE: Mouse wheel zoom already handled by line 105 with this.handleZoomBound
    // Removed duplicate wheel listener to prevent double-processing
  }
  
  /**
   * zoomIn - Increases camera zoom level
   * Moves camera closer to the subject
   */
  zoomIn() {
    this.zoomLevel = Math.min(this.zoomLevel * 1.1, 3.0);
    this.updateCameraZoom();
  }
  
  /**
   * zoomOut - Decreases camera zoom level
   * Moves camera further from the subject
   */
  zoomOut() {
    this.zoomLevel = Math.max(this.zoomLevel * 0.9, 0.5);
    this.updateCameraZoom();
  }
  
  /**
   * updateCameraZoom - Updates camera position based on zoom level
   * Adjusts perspective while maintaining the focal point
   */
  updateCameraZoom() {
    // Adjust camera position based on zoom level
    this.camera.position.z = 3.0 / this.zoomLevel;
    this.camera.updateProjectionMatrix();
  }
  
  /**
   * createTextLabel - Creates a text label in 3D space
   * Generates a sprite with text rendered as a canvas texture
   */
  createTextLabel(text, position, size, color = 0x000000) {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    
    const ctx = canvas.getContext('2d');
    
    // Clear background
    ctx.clearRect(0, 0, 128, 128);
    
    // Draw text with no background
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.font = 'bold 80px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 64, 64);
    
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true
    });
    const sprite = new THREE.Sprite(material);
    
    sprite.position.set(position.x, position.y, position.z);
    sprite.scale.set(size, size, size);
    
    return sprite;
  }
  
  /**
   * createBodyPartLabel - Creates a label for body landmarks
   * Generates a styled label with rounded background for body parts
   */
  createBodyPartLabel(text, position) {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 128;
    
    const ctx = canvas.getContext('2d');
    
    // Draw background with rounded corners
    ctx.fillStyle = 'rgba(33, 150, 243, 0.7)';
    ctx.roundRect(0, 0, 256, 128, 20);
    ctx.fill();
    
    // Draw text
    ctx.fillStyle = 'white';
    ctx.font = 'bold 60px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 128, 64);
    
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true
    });
    const sprite = new THREE.Sprite(material);
    
    sprite.position.copy(position);
    sprite.position.y += 0.1; // Position slightly above the joint
    sprite.scale.set(0.2, 0.1, 0.1);
    
    this.labels.push(sprite);
    this.scene.add(sprite);
    
    return sprite;
  }
  
  /**
   * createLimb - Creates a 3D mesh representing a body limb
   * Generates a capsule geometry between two points for visualization
   */
  createLimb(start, end, radius, color) {
    // Calculate direction and length
    const direction = new THREE.Vector3().subVectors(end, start);
    const length = direction.length();
    
    // Create a capsule geometry
    const geometry = new THREE.CapsuleGeometry(radius, length - (radius * 2), 8, 8);
    const material = new THREE.MeshPhongMaterial({ 
      color: color,
      shininess: 30,
      transparent: true,
      opacity: 0.85
    });
    
    const limb = new THREE.Mesh(geometry, material);
    
    // Position and orient the limb
    limb.position.copy(start).add(end).multiplyScalar(0.5);
    limb.quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      direction.clone().normalize()
    );
    
    this.bodyMeshes.push(limb);
    this.scene.add(limb);
    
    return limb;
  }
  
  /**
   * updatePose - Updates the 3D visualization with new pose data
   * Renders joints and connections based on provided landmark positions
   */
  updatePose(worldLandmarks) {
    if (!worldLandmarks || worldLandmarks.length === 0) return;
    
    // Clear previous visualization
    this.clear();
    
    // Scale factor for landmarks to ensure consistent size
    const scaleFactor = 1.0;
    
    // Process landmarks with proper scaling
    const processedLandmarks = [];
    
    worldLandmarks.forEach((landmark, index) => {
      // Skip low visibility points
      if (landmark.visibility && landmark.visibility < 0.5) return;
      
      // Transform coordinates with consistent scaling
      const position = new THREE.Vector3(
        -landmark.x * scaleFactor,
        -landmark.y * scaleFactor + 1,
        -landmark.z * scaleFactor
      );
      
      processedLandmarks[index] = position;
      
      // Set color to bright yellow for all joint spheres
      const color = 0xFFCC00; // Yellow
      
      // Create joint sphere with emissive material for glow effect
      const jointGeometry = new THREE.SphereGeometry(0.04, 16, 16);
      const jointMaterial = new THREE.MeshPhongMaterial({ 
        color: color,
        emissive: 0xFFCC00,
        emissiveIntensity: 0.5,
        shininess: 80,
        transparent: true,
        opacity: 0.9
      });
      
      const joint = new THREE.Mesh(jointGeometry, jointMaterial);
      joint.position.copy(position);
      
      this.scene.add(joint);
      this.joints.push(joint);
      this.pointMeshes[index] = joint;
      
      // Add joint label if needed
      if (this.bodyPartNames[index]) {
        this.createBodyPartLabel(this.bodyPartNames[index], position);
      }
    });
    
    // Create connections between joints with orange color
    this.connections.forEach(connection => {
      const [i, j] = connection;
      
      if (processedLandmarks[i] && processedLandmarks[j]) {
        const start = processedLandmarks[i];
        const end = processedLandmarks[j];
        
        // Use orange color for all connections
        const orangeColor = 0xFF7700;
        
        // Create bone with emissive material for glow
        const boneLength = start.distanceTo(end);
        const boneGeometry = new THREE.CylinderGeometry(0.015, 0.015, boneLength, 8);
        const boneMaterial = new THREE.MeshPhongMaterial({ 
          color: orangeColor,
          emissive: orangeColor,
          emissiveIntensity: 0.3,
          shininess: 30,
          transparent: true,
          opacity: 0.85
        });
        
        const bone = new THREE.Mesh(boneGeometry, boneMaterial);
        
        // Position and orient the bone to connect the joints
        const midpoint = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
        bone.position.copy(midpoint);
        
        // Orient the bone to point from start to end
        bone.lookAt(end);
        bone.rotateX(Math.PI / 2);
        
        this.scene.add(bone);
        this.bones.push(bone);
      }
    });
    
    // Add a few ambient and directional lights for overall scene lighting
    // instead of per-joint point lights
    if (!this.lightsAdded) {
      // Add ambient light for general illumination
      const ambientLight = new THREE.AmbientLight(0x404040, 1.5);
      this.scene.add(ambientLight);
      
      // Add directional lights from different angles
      const directions = [
        new THREE.Vector3(1, 1, 1),
        new THREE.Vector3(-1, -1, -1),
        new THREE.Vector3(1, -1, 1)
      ];
      
      directions.forEach(dir => {
        const dirLight = new THREE.DirectionalLight(0xFFFFFF, 0.8);
        dirLight.position.copy(dir).multiplyScalar(2);
        this.scene.add(dirLight);
      });
      
      this.lightsAdded = true;
    }
  }
  
  /**
   * createVolumetricBody - Creates a 3D mesh body representation
   * Generates a simplified volumetric body from landmark positions
   */
  createVolumetricBody(landmarks) {
    // Only create volumetric body if we have enough landmarks
    if (!landmarks[11] || !landmarks[12] || !landmarks[23] || !landmarks[24]) return;
    
    // Create torso (simplified as a box)
    const shoulderMidpoint = new THREE.Vector3().addVectors(landmarks[11], landmarks[12]).multiplyScalar(0.5);
    const hipMidpoint = new THREE.Vector3().addVectors(landmarks[23], landmarks[24]).multiplyScalar(0.5);
    const torsoHeight = shoulderMidpoint.distanceTo(hipMidpoint);
    const shoulderWidth = landmarks[11].distanceTo(landmarks[12]);
    const hipWidth = landmarks[23].distanceTo(landmarks[24]);
    
    // Create torso as a tapered cylinder
    const torsoGeometry = new THREE.CylinderGeometry(
      shoulderWidth * 0.4, // top radius
      hipWidth * 0.4,      // bottom radius
      torsoHeight,         // height
      12,                  // radial segments
      2,                   // height segments
      false                // open-ended
    );
    
    const torsoMaterial = new THREE.MeshPhongMaterial({
      color: 0x607D8B,
      transparent: true,
      opacity: 0.7,
      shininess: 30
    });
    
    const torso = new THREE.Mesh(torsoGeometry, torsoMaterial);
    
    // Position torso
    torso.position.copy(new THREE.Vector3().addVectors(shoulderMidpoint, hipMidpoint).multiplyScalar(0.5));
    torso.quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3().subVectors(shoulderMidpoint, hipMidpoint).normalize().negate()
    );
    
    this.bodyMeshes.push(torso);
    this.scene.add(torso);
    
    // Create limbs as capsules
    // Arms
    if (landmarks[11] && landmarks[13]) {
      this.createLimb(landmarks[11], landmarks[13], 0.025, 0x2196F3); // Left upper arm
    }
    if (landmarks[13] && landmarks[15]) {
      this.createLimb(landmarks[13], landmarks[15], 0.02, 0x2196F3);  // Left forearm
    }
    if (landmarks[12] && landmarks[14]) {
      this.createLimb(landmarks[12], landmarks[14], 0.025, 0xFF9800); // Right upper arm
    }
    if (landmarks[14] && landmarks[16]) {
      this.createLimb(landmarks[14], landmarks[16], 0.02, 0xFF9800);  // Right forearm
    }
    
    // Legs
    if (landmarks[23] && landmarks[25]) {
      this.createLimb(landmarks[23], landmarks[25], 0.035, 0x2196F3); // Left thigh
    }
    if (landmarks[25] && landmarks[27]) {
      this.createLimb(landmarks[25], landmarks[27], 0.03, 0x2196F3);  // Left calf
    }
    if (landmarks[24] && landmarks[26]) {
      this.createLimb(landmarks[24], landmarks[26], 0.035, 0xFF9800); // Right thigh
    }
    if (landmarks[26] && landmarks[28]) {
      this.createLimb(landmarks[26], landmarks[28], 0.03, 0xFF9800);  // Right calf
    }
  }
  
  /**
   * clear - Removes all existing visualization elements
   * Cleans up the scene for the next frame update
   */
  clear(preserveWallLabels = false) {
    // Remove all joints and bones
    this.joints.forEach(joint => this.scene.remove(joint));
    this.bones.forEach(bone => this.scene.remove(bone));
    this.bodyMeshes.forEach(mesh => this.scene.remove(mesh));
    
    // Conditionally clear labels
    if (!preserveWallLabels) {
      this.labels.forEach(label => this.scene.remove(label));
      this.labels = [];
    } else {
      // Only remove non-wall labels if we're preserving wall labels
      const wallLabels = this.labels.filter(label => label.userData.isWallLabel);
      const otherLabels = this.labels.filter(label => !label.userData.isWallLabel);
      
      otherLabels.forEach(label => this.scene.remove(label));
      this.labels = wallLabels;
    }
    
    this.joints = [];
    this.bones = [];
    this.bodyMeshes = [];
    this.pointMeshes = {};
  }
  
  /**
   * animate - Runs the animation loop for continuous rendering
   * Updates the scene at each animation frame
   */
  animate() {
    requestAnimationFrame(() => this.animate());
    
    // Fixed camera position, no rotation
    this.renderer.render(this.scene, this.camera);
  }
  
  /**
   * resize - Resizes the renderer to match container dimensions
   * Updates camera aspect ratio for proper rendering
   */
  resize(width, height) {
    if (!this.renderer) return;
    
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(width, height);
  }
  
  /**
   * onWindowResize - Handles window resize events
   * Adjusts canvas and camera to maintain proper display
   */
  onWindowResize() {
    if (!this.canvas) return;
    
    const container = this.canvas.parentElement;
    if (!container) return;
    
    // Get container dimensions
    const width = container.clientWidth;
    const height = container.clientHeight;
    
    // Update canvas dimensions
    this.canvas.width = width;
    this.canvas.height = height;
    
    // Update camera
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    
    // Update renderer
    this.renderer.setSize(width, height);
  }
  
  /**
   * createWallLabel - Creates a label for reference walls
   * Generates a text label oriented to the wall surface
   */
  createWallLabel(text, position, size, rotationY = 0) {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 128;
    
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, 512, 128);
    
    // Add semi-transparent background
    ctx.fillStyle = 'rgba(220, 220, 220, 0.7)';
    ctx.fillRect(0, 0, 512, 128);
    
    // Add border
    ctx.strokeStyle = 'rgba(150, 150, 150, 0.8)';
    ctx.lineWidth = 4;
    ctx.strokeRect(2, 2, 508, 124);
    
    // Add text
    ctx.fillStyle = '#333333';
    ctx.font = 'bold 48px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 256, 64);
    
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true
    });
    const sprite = new THREE.Sprite(material);
    
    sprite.position.set(position.x, position.y, position.z);
    sprite.scale.set(size * 4, size, 1);
    
    // Apply rotation if needed
    if (rotationY !== 0) {
      // For rotated wall labels, we need a different approach
      // Create a group to hold the sprite
      const group = new THREE.Group();
      group.add(sprite);
      group.rotation.y = rotationY;
      this.scene.add(group);
    } else {
      this.scene.add(sprite);
    }
    
    this.labels.push(sprite);
    
    return sprite;
  }
  
  /**
   * handleZoom - Processes zoom wheel events
   * Updates zoom level based on wheel scrolling
   */
  handleZoom(event) {
    event.preventDefault();
    
    // Determine zoom direction
    const zoomDirection = event.deltaY > 0 ? -1 : 1;
    
    // Update zoom level
    this.zoomLevel += zoomDirection * this.zoomSpeed;
    
    // Clamp zoom level to min/max
    this.zoomLevel = Math.max(this.minZoom, Math.min(this.maxZoom, this.zoomLevel));
    
    // Update camera position based on zoom
    const originalPosition = new THREE.Vector3(1.5, 1.5, 3.0);
    const lookAtPoint = new THREE.Vector3(0, 1, 0);
    
    // Get direction vector from lookAt point to original position
    const direction = new THREE.Vector3().subVectors(originalPosition, lookAtPoint).normalize();
    
    // Scale the direction vector by zoom level - but keep skeleton size consistent
    const distance = 3.0 / this.zoomLevel;
    direction.multiplyScalar(distance);
    
    // Set new camera position
    this.camera.position.copy(lookAtPoint).add(direction);
    this.camera.lookAt(lookAtPoint);
  }
  
  /**
   * addWallLabel - Adds a labeled reference point to a wall
   * Creates a text sprite at the specified position
   */
  addWallLabel(text, position, color) {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 128, 128);
    
    ctx.fillStyle = `#${color.toString(16).padStart(6, '0')}`;
    ctx.font = 'bold 80px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 64, 64);
    
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.SpriteMaterial({ 
      map: texture,
      transparent: true
    });
    const sprite = new THREE.Sprite(material);
    
    sprite.position.copy(position);
    sprite.scale.set(0.3, 0.3, 1);
    
    this.scene.add(sprite);
    
    return sprite;
  }
}

export default PoseVisualizer; 